import type { PostHog } from "posthog-js";

// posthog-js is not part of appBundle (it was ~16% of it); the self-hosted
// ES5 dist (static/js/posthog.es5.js, copy of posthog-js/dist/array.full.es5.js)
// is injected LOAD_DELAY_MS after the first init() call so it never competes
// with app boot for network/CPU. Calls made before that are queued (with their
// original timestamps) and replayed once the script loads.

const SCRIPT_SRC = "./static/js/posthog.es5.js";
const LOAD_DELAY_MS = 8000;
const RETRY_DELAY_MS = 30000;
const MAX_LOAD_ATTEMPTS = 3;
const MAX_QUEUE = 200;

let queue: Array<{ method: string; args: unknown[]; at: Date }> | null = [];
let loadScheduled = false;
let loadAttempts = 0;

const real = () => (window as any).posthog;

const invoke = (method: string, args: unknown[]) => {
    try {
        const lib = real();
        if (lib && typeof lib[method] === "function") {
            lib[method].apply(lib, args);
        }
    } catch (e) {
        console.error("PostHog error:", e);
    }
};

const flushQueue = () => {
    const pending = queue;
    queue = null;
    if (!pending) return;
    pending.forEach((entry) => {
        let args = entry.args;
        if (entry.method === "capture") {
            // keep the original event time instead of the flush time
            const opts = args[2] && typeof args[2] === "object" ? (args[2] as object) : {};
            args = [args[0], args[1], { timestamp: entry.at, ...opts }];
        }
        invoke(entry.method, args);
    });
};

const injectScript = () => {
    loadAttempts++;
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = flushQueue;
    script.onerror = () => {
        console.error("Failed to load posthog script (attempt " + loadAttempts + ")");
        if (loadAttempts < MAX_LOAD_ATTEMPTS) {
            setTimeout(injectScript, RETRY_DELAY_MS);
        } else {
            queue = null;
        }
    };
    document.head.appendChild(script);
};

const scheduleLoad = () => {
    if (loadScheduled) return;
    loadScheduled = true;
    setTimeout(injectScript, LOAD_DELAY_MS);
};

const call =
    (method: string) =>
    (...args: unknown[]) => {
        if (queue) {
            if (queue.length < MAX_QUEUE) {
                queue.push({ method, args, at: new Date() });
            }
            return;
        }
        invoke(method, args);
    };

const posthog = {
    init: (...args: unknown[]) => {
        call("init")(...args);
        scheduleLoad();
    },
    identify: call("identify"),
    capture: call("capture")
};

export default posthog as unknown as PostHog;
